import React, { useState } from "react";
import DataTable from "../../../components/common/DataTable";
import { resolveErrorStatus } from "../../../services/mongo/taskLogs";
import { TABLE_COLUMNS } from "../constants/logConstants";
import DeleteErrorsDialog from "./DeleteErrorsDialog";
import ErrorButtons from "./ErrorButtons";

/**
 * ErrorLogsTable component displays error logs with selection, filtering, and resolution functionality
 *
 * This component encapsulates all error handling functionality including:
 * - Displaying error logs in a data table
 * - Filtering logs based on search text
 * - Selecting individual or all errors
 * - Resolving (marking as fixed) selected errors
 * - Confirming error resolution via dialog
 *
 * @param {Object} props - Component props
 * @param {Array} props.logs - The array of error logs to display
 * @param {string} props.filterQuery - The current filter text to apply to logs
 * @param {Function} props.fetchErrorList - Function to refresh error list after updates
 * @param {Function} props.showAlert - Function to display alert messages to the user
 * @returns {JSX.Element} Rendered error logs table with selection and action controls
 */
function ErrorLogsTable({ logs, filterQuery, fetchErrorList, showAlert }) {
  const [selectedErrorIds, setSelectedErrorIds] = useState([]);
  const [dialogBoxOpen, setDialogBoxOpen] = useState(false);

  /** Toggles the selection state of an error ID by adding or removing it from the selectedErrorIds array */
  const toggleErrorSelection = (errorId) => {
    setSelectedErrorIds((currentSelected) => {
      const isAlreadySelected = currentSelected.includes(errorId);
      return isAlreadySelected
        ? currentSelected.filter((id) => id !== errorId) // Remove if selected
        : [...currentSelected, errorId]; // Add if not selected
    });
  };

  /** Selects all errors if not all are selected, otherwise deselects all */
  const selectAllErrors = () => {
    const regex = new RegExp(filterQuery, "i");
    const filteredErrors = logs.filter((obj) => regex.test(Object.values(obj)));

    setSelectedErrorIds(
      selectedErrorIds.length >= filteredErrors.length
        ? [] // Deselect all if all are selected
        : filteredErrors.map((error) => error.id) // Select all filtered errors
    );
  };

  /** Resolves an error by updating its status and refetching the error list */
  const resolveError = async (errorId) => {
    await resolveErrorStatus(errorId);
    await fetchErrorList();
    setSelectedErrorIds([]);
    setDialogBoxOpen(false);
  };

  const displayFilteredLogs = () => {
    if (!logs) return [];
    const regex = new RegExp(filterQuery, "i");
    return logs.filter((obj) => regex.test(Object.values(obj)));
  };

  return (
    <>
      <DeleteErrorsDialog
        open={dialogBoxOpen}
        onClose={() => setDialogBoxOpen(false)}
        onConfirm={resolveError}
        selectedErrorIds={selectedErrorIds}
      />

      <ErrorButtons
        selectAllErrors={selectAllErrors}
        selectedErrorIds={selectedErrorIds}
        showAlert={showAlert}
        setDialogBoxOpen={setDialogBoxOpen}
      />

      <DataTable
        columns={TABLE_COLUMNS.errors(selectedErrorIds, toggleErrorSelection)}
        dataRows={displayFilteredLogs()}
        height={"70vh"}
      />
    </>
  );
}

export default ErrorLogsTable;
